import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "react-toastify";
import {
  MdAdd,
  MdCalendarToday,
  MdClear,
  MdFilterAlt,
  MdAccountBalanceWallet,
  MdArrowBack,
} from "react-icons/md";
import { format } from "date-fns";
import {
  addCredit,
  fetchHotelCredit,
  getCurrentLocalDateTime,
  sucessCheckin,
} from "../utils/helperFunctions";

const AddCredit = () => {
  const { hotelId } = useParams();
  const location = useLocation();
  const hotelName = location.state?.hotelName;
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(false);
  const [creditForm, setCreditForm] = useState({
    amount: "",
  });
  const { date } = getCurrentLocalDateTime();
  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
  });
  const [showCreditModal, setShowCreditModal] = useState(false);

  // Stats State
  const [stats, setStats] = useState({
    successfulCheckinCount: 0,
    creditBalance: 0,
  });
  const [creditDetails, setCreditDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  // const [statsLoading, setStatsLoading] = useState(false);

  // Fetch stats based on date range
  const fetchStats = async () => {
    // setStatsLoading(true);
    try {
      const response = await sucessCheckin({ ...filters, hotelId });
      setStats(response.data);
    } catch (error) {
      toast.error("Failed to load statistics");
      console.error("Error fetching stats:", error);
    } 
    // finally {
    //   setStatsLoading(false);
    // }
  };

  useEffect(() => {
    // const hasFromDate = !!filters.fromDate;
    // const hasToDate = !!filters.toDate;

    // // Call API only if both are present OR both are absent
    // if (hasFromDate === hasToDate) {
      fetchStats();
    // }
  }, [filters]);

  const fetchCreditData = async () => {
    try {
      const res = await fetchHotelCredit({ hotelId });

      if (res?.status) {
        setCreditDetails(res.data); // state to store credit history
      }
    } catch (error) {
      console.error("Error fetching credit details:", error);
    }
  };


  useEffect(() => {
    if (!hotelId) return;

   
    fetchCreditData();
  }, [hotelId]);

  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showCreditModal && !loading) {
        handleCloseModal();
      }
    };
  
    // Add event listener when modal is open
    if (showCreditModal) {
      document.addEventListener('keydown', handleEscKey);
    }
  
    // Clean up event listener
    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [showCreditModal, loading]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => {
      const newFilters = {
        ...prev,
        [name]: value,
      };
      if (
        name === "fromDate" &&
        value &&
        newFilters.toDate &&
        new Date(value) > new Date(newFilters.toDate)
      ) {
        newFilters.toDate = value; // Set toDate equal to fromDate
      }

      if (
        name === "toDate" &&
        value &&
        newFilters.fromDate &&
        new Date(value) < new Date(newFilters.fromDate)
      ) {
        newFilters.fromDate = value; // Set fromDate equal to toDate
      }

      return newFilters;
    });
  };

  const handleResetFilters = () => {
    if (filters.fromDate || filters.toDate) {
      setFilters({ fromDate: "", toDate: "" });
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setCreditForm((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!creditForm.amount || parseFloat(creditForm.amount) <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        hotelId: hotelId, // make sure hotelId exists in scope
        creditadd: creditForm.amount,
      };

      const res = await addCredit(payload);
      if (res?.status) {
        toast.success(res.msg);
        handleCloseModal();
        fetchStats();
        fetchCreditData();

      }
    } catch (error) {
      toast.error(error.msg);
      console.error("Error adding credit:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFormShow = () => {
    setShowCreditModal(true);
  };

  const handleCloseModal = () => {
    setShowCreditModal(false);
    setCreditForm({ amount: "" });
  };

  return (
    <div className="p-4 md:p-6 bg-gray-50 ">
      <div className="container mx-auto mb-6 bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl shadow-sm overflow-hidden">
        <div className="p-6">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
            <div className="flex items-center">
              {/* Back Arrow Button with React Icon */}
              <button
                onClick={() => navigate("/hotel")}
                className="mr-3 p-2 rounded-full hover:bg-gray-100 transition-colors duration-200"
                title="Go back to Hotels"
                aria-label="Go back to Hotels"
              >
               <MdArrowBack className="h-5 w-5 text-gray-600 hover:text-indigo-600" />
              </button>

              {/* Title and Description */}
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
                  Manage Credit
                </h1>
                <p className="text-sm text-gray-600 mt-1">
                  Manage credit and view check-in statistics for {hotelName}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <button
                onClick={() => {
                  setShowFilters(!showFilters);
                  handleResetFilters();
                }}
                className="flex items-center gap-2 px-4 py-2 bg-white text-indigo-600 border border-indigo-200 rounded-lg hover:bg-indigo-50 transition-colors shadow-sm"
              >
                {showFilters ? (
                  <MdClear className="text-lg" />
                ) : (
                  <MdFilterAlt className="text-lg" />
                )}
                {showFilters ? "Hide Filters" : "Filters"}
              </button>
              <button
                onClick={handleFormShow}
                className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors shadow-sm"
              >
                <MdAdd className="text-lg" />
                Add Credit
              </button>
            </div>
          </div>
          {/* Filters Section */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: "auto" }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.3 }}
              className="bg-white p-6 rounded-lg shadow-sm border border-indigo-300 mb-6"
            >
              <div className="flex justify-end mb-4">
                {Object.values(filters).some(
                  (val) => val !== "" && val !== null
                ) && (
                  <button
                    onClick={handleResetFilters}
                    className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-800"
                  >
                    <MdClear className="text-lg" />
                    Clear all
                  </button>
                )}
              </div>
              <div>
                <div className="grid grid-cols-1 sm:grid-cols-2  gap-4">
                  <div className="space-y-1 relative">
                    <label className="block text-sm font-medium text-indigo-500">
                      From Date
                    </label>
                    <div className="relative w-full">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-20">
                        <MdCalendarToday className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        readOnly
                        placeholder="DD/MM/YYYY"
                        value={
                          filters.fromDate
                            ? format(new Date(filters.fromDate), "dd/MM/yyyy")
                            : ""
                        }
                        onClick={() =>
                          document
                            .getElementById("from-date-picker")
                            .showPicker()
                        }
                        className="absolute top-0 left-0 w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-black cursor-pointer focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm z-10"
                      />
                      <input
                        type="date"
                        id="from-date-picker"
                        name="fromDate"
                        max={date}
                        value={filters.fromDate}
                        onChange={handleFilterChange}
                        className="w-full pl-10 pr-3 py-1.5 border border-gray-300 rounded-md shadow-sm bg-transparent text-transparent caret-transparent z-0 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-indigo-500">
                      To Date
                    </label>
                    <div className="relative w-full">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none z-20">
                        <MdCalendarToday className="text-gray-400" />
                      </div>
                      <input
                        type="text"
                        readOnly
                        value={
                          filters.toDate
                            ? format(new Date(filters.toDate), "dd/MM/yyyy")
                            : ""
                        }
                        placeholder="DD/MM/YYYY"
                        onClick={() => {
                          filters.fromDate &&
                            document
                              .getElementById("to-date-picker")
                              ?.showPicker();
                        }}
                        className={`absolute top-0 left-0 w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-black cursor-pointer focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm z-10 ${
                          !filters.fromDate
                            ? "bg-gray-50 text-gray-500 cursor-not-allowed"
                            : ""
                        }`}
                      />
                      <input
                        type="date"
                        id="to-date-picker"
                        name="toDate"
                        value={filters.toDate}
                        onChange={handleFilterChange}
                        max={date}
                        disabled={!filters.fromDate}
                        className="w-full pl-10 pr-3 py-1.5 border border-gray-300 rounded-md shadow-sm bg-transparent text-transparent caret-transparent z-0 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 text-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
            {/* Successful Check-ins Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Successful Check-ins
                  </p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">
                    {stats.successfulCheckinCount}
                  </p>
                  <div className="flex items-center mt-2 text-xs text-green-500">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-3 w-3 mr-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <span>Completed bookings</span>
                  </div>
                </div>
                <div className="bg-green-100 p-3 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-green-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>

            {/* Hotel Remaining Credit Card */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: 0.1 }}
              className="bg-white p-4 rounded-lg border border-gray-200 shadow-sm"
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-500">
                    Remaining Credit
                  </p>
                  <p className="text-2xl font-bold text-gray-800 mt-1">
                    {stats.creditBalance?.toLocaleString() || "0"}
                  </p>
                  <div className="grid grid-cols-2 gap-1 mt-2 text-xs text-gray-500">
                    <div className="flex items-center">
                      <div
                        className={`w-2 h-2 rounded-full mr-1 ${
                          stats.creditBalance !== null &&
                          stats.creditBalance < 1000
                            ? "bg-red-500"
                            : "bg-green-500"
                        }`}
                      ></div>
                      <span>
                        {stats.creditBalance !== null &&
                        stats.creditBalance < 1000
                          ? "Low balance"
                          : "Good"}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <div className="w-2 h-2 rounded-full bg-blue-500 mr-1"></div>
                      <span>Available</span>
                    </div>
                  </div>
                </div>
                <div className="bg-blue-100 p-3 rounded-full">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-blue-600"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <div className=" mx-auto bg-white shadow-sm overflow-hidden">
          <div className="p-4 md:p-6">
            {loading && creditDetails.length === 0 ? (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
              </div>
            ) : creditDetails.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <p className="mt-4 text-lg font-medium text-gray-500">
                  No credit data found
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 shadow-md">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-indigo-50 to-blue-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Added At
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Credit Balance
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-bold text-indigo-600 uppercase tracking-wider border-b border-indigo-100">
                        Added By
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {creditDetails.map((credit, index) => (
                      <motion.tr
                        key={credit._id}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className={`transition-colors duration-150 ${
                          index % 2 === 0 ? "bg-white" : "bg-indigo-50/30"
                        } hover:bg-indigo-100/30`}
                      >
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {new Date(credit.createdAt)
                            .toLocaleDateString("en-GB", {})
                            .replace(/\//g, "-")}{" "}
                          {new Date(credit.createdAt).toLocaleTimeString(
                            "en-US",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                              hour12: true,
                            }
                          )}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {credit.creditadd}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {credit.addedBy.name}
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
      {showCreditModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 transition-opacity"
              aria-hidden="true"
            >
              <div
                className="absolute inset-0 bg-gray-500 opacity-75"
                onClick={() => {
                  if (!loading) {
                    setShowCreditModal(false);
                  }
                }}
              ></div>
            </div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg w-full mx-2 sm:mx-0">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-3">
                    <div className="bg-gradient-to-r from-indigo-600 to-blue-600 p-2 rounded-lg">
                      <MdAccountBalanceWallet className="text-xl text-white" />
                    </div>
                    <div>
                      <h3 className="text-lg leading-6 font-medium text-gray-900">
                        Add Credit
                      </h3>
                      <p className="text-sm text-gray-500 mt-1">
                        Hotel: {hotelName}
                      </p>
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="bg-gradient-to-r from-indigo-50 to-blue-50 p-4 rounded-lg">
                    <div className="space-y-4">
                      <div>
                        <div className="relative">
                          <input
                            type="text"
                            name="amount"
                            value={creditForm.amount}
                            onChange={handleInputChange}
                            placeholder="1000"
                            className="px-4 py-2.5 w-full text-sm border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                            required
                            disabled={loading}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                    <button
                      type="submit"
                      disabled={
                        loading ||
                        !creditForm.amount ||
                        parseFloat(creditForm.amount) <= 0
                      }
                      className={`inline-flex justify-center items-center rounded-md border border-transparent py-2 px-4 text-sm font-medium text-white shadow-sm focus:outline-none ${
                        loading ||
                        !creditForm.amount ||
                        parseFloat(creditForm.amount) <= 0
                          ? "bg-indigo-400 cursor-not-allowed"
                          : "bg-indigo-600 hover:bg-indigo-700"
                      }`}
                    >
                      {loading ? (
                        <>
                          <svg
                            className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                            fill="none"
                            viewBox="0 0 24 24"
                          >
                            <circle
                              className="opacity-25"
                              cx="12"
                              cy="12"
                              r="10"
                              stroke="currentColor"
                              strokeWidth="4"
                            ></circle>
                            <path
                              className="opacity-75"
                              fill="currentColor"
                              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                          </svg>
                          Adding...
                        </>
                      ) : (
                        <>
                          <MdAdd className="w-4 h-4 mr-2" />
                          Add Credit
                        </>
                      )}
                    </button>
                    <button
                      type="button"
                      disabled={loading}
                      onClick={handleCloseModal}
                      className="inline-flex justify-center rounded-md border border-gray-300 bg-white py-2 px-4 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AddCredit;
